import React, { useEffect, useState } from 'react';
import { Box, Dialog, Typography, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FlagIcon from '@mui/icons-material/Flag';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import { toast } from 'react-toastify';
import { API } from '../../../../api';
import ConfirmDialog from '../../../../components/DialogUi/ConfirmDialog';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';
import { useAppSelector } from '../../../../redux/hooks';
import {
  canRemove,
  removalMessage,
  removalTitle,
  removedToast
} from '../Program/components/learning-lifecycle/program-editor/peopleRemoval';
import { ProgramSummary } from './ProgramsView';
import MetricButton from './MetricButton';
import MemberRiskAreasModal from './MemberRiskAreasModal';
import ModalSelect from './ModalSelect';
import useCapabilityRelease from '../../../../hooks/useCapabilityRelease';
import { MemberRole, useMemberWord } from '../../../../utils/memberTypeLabels';
import { scoreOrDash } from '../../../../utils/requirementsScore';
import { MOBILE_SX, useIsMobileApp } from '../../../../mobile/mobileApp';
import { TapTooltip } from '../../../../components/ui/DosenTooltip';

// Shape from GET /getOrgMembersProgramProgress/{orgId} → members[].programs[]
export interface IMemberProgram {
  id: string;
  name: string;
  progress: number; // 0–100 goal progress
  programModel: string; // e.g. "masterclass", "mentorship", "masterclass&mentorship"
  /** Locked by an admin — left out of the member's Learning Range. */
  locked?: boolean;
  /** 0–100. Null when nothing in the program assesses them (Audience Requirements, DA-5056 R5): shown "-". */
  mcqScore: number | null;
  /** Unresolved lessons in this program (A-3) */
  unresolvedFlags?: number;
  /** Withheld-answer MCQ mode (drives the first-pass → current transition) */
  rationaleEnabled?: boolean;
  firstPassScore?: number | null;
  currentScore?: number | null;
  /** Their role in it: admin when they are one of its admins too. Absent from a BE that predates the Role column. */
  role?: ProgramRole;
  /** An admin of it and not a learner: nothing to progress through (the modal's own rows, from adminPrograms). */
  adminOnly?: boolean;
}

type ProgramRole = 'mentee' | 'admin';

// Shape from GET /getOrgMembersProgramProgress/{orgId} → members[].adminPrograms[]
export interface IMemberAdminProgram {
  id: string;
  name: string;
  programModel: string;
  role: 'admin';
}

/** The member's programs as rows: those they learn in, then those they are only an admin of. */
const programRows = (programs: IMemberProgram[], adminPrograms: IMemberAdminProgram[] = []): IMemberProgram[] => [
  ...programs,
  ...adminPrograms.map((p) => ({ ...p, progress: 0, mcqScore: null, adminOnly: true }))
];

interface MemberProgramsModalProps {
  open: boolean;
  onClose: () => void;
  memberName: string;
  /** Member whose programs these are — drives the red-flag drill-down and allocation. */
  memberId?: string;
  programs: IMemberProgram[];
  /** The programs they are only an admin of, listed after the rest with their role. */
  adminPrograms?: IMemberAdminProgram[];
  // Manage mode (orgLevelAdmin): when authId is provided, the modal lets the
  // admin allocate the member to further programs (BE-3), and take them out of one.
  authId?: string;
  allPrograms?: ProgramSummary[];
  /** An org admin reaches every program through the organization: Remove is never offered on them. */
  memberIsOrgAdmin?: boolean;
  onChanged?: () => void;
}

// Role codes sent to allocateMemberToProgram; the labels are the org's own words (built in the component).
const ROLE_CODES: MemberRole[] = ['mentee', 'mentor', 'admin'];

/** Format backend model string into a readable label */
const formatProgramModel = (model: string): string => {
  switch (model?.toLowerCase()) {
    case 'masterclass':
      return 'Course';
    case 'mentorship':
      return 'Mentorship';
    case 'masterclass&mentorship':
    case 'masterclass & mentorship':
      return 'Guided Course';
    case 'guided-masterclass':
      return 'Guided Course';
    default:
      return model ? model.replace(/\b\w/g, (c) => c.toUpperCase()) : 'Not set';
  }
};

const getProgressColor = (score: number): string => {
  if (score >= 80) return '#00972D';
  if (score >= 50) return '#F0B80D';
  if (score >= 30) return '#F0B80D';
  return '#FF0000';
};

const headerLabelSx = {
  fontSize: 12,
  fontWeight: 400,
  color: '#B6B6B6',
  fontFamily: 'Inter, sans-serif',
  lineHeight: '15.60px',
  whiteSpace: 'nowrap' as const
};

const cellTextSx = {
  fontSize: 14,
  fontWeight: 600,
  color: '#FDFDFD',
  fontFamily: 'Inter, sans-serif',
  whiteSpace: 'nowrap' as const,
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

// The name column takes whatever the fixed columns leave, so the row always fits
// the modal (long names ellipsize).
const NAME_COL = { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px' };

/**
 * The quiz score as the table writes it: first pass → current after a retake, else the one score (a dash when none).
 * The phone's rows use this; the desktop table spells the same rule out inline (its Quiz score cell). Change both together.
 */
const quizScoreText = (program: IMemberProgram) =>
  program.rationaleEnabled &&
  typeof program.firstPassScore === 'number' &&
  typeof program.currentScore === 'number' &&
  program.firstPassScore !== program.currentScore
    ? `${program.firstPassScore}% → ${program.currentScore}%`
    : scoreOrDash(
        program.rationaleEnabled && typeof program.firstPassScore === 'number'
          ? program.firstPassScore
          : program.mcqScore
      );

const ProgressBar = ({ score }: { score: number }) => {
  const clamped = Math.min(100, Math.max(0, score));
  const color = getProgressColor(clamped);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', width: 150 }}>
      <Box
        sx={{
          width: 100,
          height: 7,
          flexShrink: 0,
          borderRadius: '2px',
          overflow: 'hidden',
          outline: '1px solid #444444',
          outlineOffset: '-1px'
        }}
      >
        <Box sx={{ width: `${clamped}%`, height: '100%', bgcolor: color }} />
      </Box>
      <Typography sx={cellTextSx}>{clamped}%</Typography>
    </Box>
  );
};

/**
 * Program Progress — a member's programs with progress, quiz score and
 * unresolved red-flag count, opened from the Program Progress figure on Org
 * Members. A program's red-flag count opens just that program's red flags.
 * In manage mode a program's Remove takes the member out of it, under the
 * People tab's rules (peopleRemoval.ts), and Add to program puts them in one.
 */
const MemberProgramsModal = ({
  open,
  onClose,
  memberName,
  memberId,
  programs,
  adminPrograms,
  authId,
  allPrograms,
  memberIsOrgAdmin = false,
  onChanged
}: MemberProgramsModalProps) => {
  // The Capability release drops Unresolved Red Flags from this view (utils/capabilityRelease.ts).
  const showFlags = !useCapabilityRelease();
  const w = useMemberWord();
  const roleOptions = ROLE_CODES.map((role) => ({ value: role, label: w(role) }));
  const manage = Boolean(authId && memberId);
  const [localPrograms, setLocalPrograms] = useState<IMemberProgram[]>(() => programRows(programs, adminPrograms));
  // The program whose role is being saved.
  const [roleBusy, setRoleBusy] = useState<string | null>(null);
  const [addProgramId, setAddProgramId] = useState('');
  const [addRole, setAddRole] = useState('mentee');
  const [busy, setBusy] = useState(false);
  // The program whose red flags are open on top of this modal.
  const [riskProgram, setRiskProgram] = useState<IMemberProgram | null>(null);
  // A phone: the whole screen, each program a block with its figures labelled beneath its name.
  const isMobileApp = useIsMobileApp();
  const viewerId = useAppSelector((s) => s.userProfile.data?._id) || '';
  // The program Remove asks about. Kept while the confirm fades out, so its text doesn't empty first.
  const [removing, setRemoving] = useState<IMemberProgram | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removeBusy, setRemoveBusy] = useState(false);

  useEffect(() => {
    setLocalPrograms(programRows(programs, adminPrograms));
  }, [programs, adminPrograms, open]);

  // Programs the member isn't already in — candidates for allocation. A program for its roles only
  // takes its learners through their roles: only an admin can be added to it.
  const enrolledIds = new Set(localPrograms.map((p) => p.id));
  const addCandidates = (allPrograms || []).filter(
    (p) => !enrolledIds.has(p.id) && (addRole === 'admin' || !p.rolesOnly)
  );
  useEffect(() => {
    if (addProgramId && !addCandidates.some((p) => p.id === addProgramId)) setAddProgramId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addRole]);

  // Remove, as on the program's People tab. A program missing from the admin's list has no known
  // owner (the member may be it), so it isn't offered there.
  const removableFrom = (program: IMemberProgram): boolean => {
    const known = (allPrograms || []).find((p) => p.id === program.id);
    return (
      !!known &&
      canRemove(
        { id: memberId || '', invited: false },
        {
          canEdit: manage,
          viewerId,
          ownerId: known.ownerId ?? null,
          orgAdminIds: new Set(memberIsOrgAdmin && memberId ? [memberId] : [])
        }
      )
    );
  };
  const askRemove = (program: IMemberProgram) => {
    setRemoving(program);
    setConfirmOpen(true);
  };
  const handleRemove = async () => {
    if (!manage || !removing) return;
    const program = removing;
    setRemoveBusy(true);
    try {
      await API.removeUserFromGroup({ grpId: program.id, userId: memberId as string });
      setLocalPrograms((prev) => prev.filter((p) => p.id !== program.id));
      toast.success(removedToast(memberName, program.name));
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || `${memberName} couldn't be removed from ${program.name}. Try again.`);
    } finally {
      setRemoveBusy(false);
      setConfirmOpen(false);
    }
  };
  const removeButton = (program: IMemberProgram) => (
    <Tooltip title="Remove from this program" placement="bottom">
      <IconButton
        onClick={() => askRemove(program)}
        aria-label={`Remove ${memberName} from ${program.name}`}
        sx={{ width: 28, height: 28, color: '#868686', '&:hover': { color: '#FDFDFD' } }}
      >
        <PersonRemoveOutlinedIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  );

  // Role, learner or admin, changed in place. An org admin is an admin of every program and a program's
  // owner stays one of its admins (the backend refuses both); a program missing from the admin's list has
  // no known owner, so it isn't offered either.
  const roleOf = (program: IMemberProgram): ProgramRole => program.role ?? (program.adminOnly ? 'admin' : 'mentee');
  const roleEditable = (program: IMemberProgram): boolean => {
    const known = (allPrograms || []).find((p) => p.id === program.id);
    return manage && !memberIsOrgAdmin && !!known && known.ownerId !== memberId;
  };
  const changeRole = async (program: IMemberProgram, role: ProgramRole) => {
    if (!manage || roleBusy || roleOf(program) === role) return;
    setRoleBusy(program.id);
    try {
      await API.changeMemberProgramRole(authId as string, memberId as string, program.id, role);
      // An admin has nothing to progress through: the row shows dashes until the list is read again.
      setLocalPrograms((prev) => prev.map((p) => (p.id === program.id ? { ...p, role, adminOnly: role === 'admin' } : p)));
      toast.success(`${memberName}'s role in ${program.name} is now ${w(role)}.`);
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || `${memberName}'s role in ${program.name} couldn't be changed. Try again.`);
    } finally {
      setRoleBusy(null);
    }
  };
  const roleCell = (program: IMemberProgram) =>
    roleEditable(program) ? (
      <ModalSelect
        options={roleOptions.filter((o) => o.value !== 'mentor')}
        value={roleOf(program)}
        onChange={(value) => changeRole(program, value as ProgramRole)}
        disabled={roleBusy !== null}
      />
    ) : (
      <Typography sx={cellTextSx}>{w(roleOf(program))}</Typography>
    );
  const dash = <Typography sx={{ ...cellTextSx, color: '#565656' }}>-</Typography>;

  const handleAdd = async () => {
    if (!manage || busy || !addProgramId) return;
    setBusy(true);
    try {
      await API.allocateMemberToProgram(authId as string, memberId as string, {
        programId: addProgramId,
        role: addRole
      });
      const prog = (allPrograms || []).find((p) => p.id === addProgramId);
      setLocalPrograms((prev) => [
        ...prev,
        { id: addProgramId, name: prog?.name || 'Program', programModel: prog?.model || '', progress: 0, mcqScore: 0 }
      ]);
      setAddProgramId('');
      setAddRole('mentee');
      toast.success(`${memberName} was added to ${prog?.name || 'the program'}.`);
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || `${memberName} could not be added to this program. Try again.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        fullScreen={isMobileApp}
        slotProps={{
          backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.60)', backdropFilter: 'blur(10.95px)' } }
        }}
        PaperProps={{
          sx: {
            width: showFlags ? 1200 : 1040,
            maxWidth: '95vw',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#0F0F0F',
            borderRadius: '8px',
            outline: '1px solid #444444',
            outlineOffset: '-1px',
            overflow: 'hidden',
            boxShadow: 'none',
            [MOBILE_SX]: { width: '100%', maxWidth: '100%', maxHeight: '100%', m: 0, borderRadius: 0, outline: 'none' }
          }
        }}
      >
        {/* Header */}
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            pl: '25px',
            pr: '45px',
            py: '20px',
            borderBottom: '1px solid #2A2A2A',
            position: 'relative',
            [MOBILE_SX]: { pl: '16px', pr: '52px', py: '14px' }
          }}
        >
          <Typography
            sx={{ fontSize: 18, fontWeight: 700, color: '#FDFDFD', fontFamily: 'Inter, sans-serif', flex: 1 }}
          >
            {memberName}&rsquo;s Program Progress
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{
              p: 0.5,
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              [MOBILE_SX]: { p: '10px', right: 6 }
            }}
          >
            <CloseIcon sx={{ fontSize: 20, color: '#FDFDFD' }} />
          </IconButton>
        </Box>

        {/* Body (scrollable) */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            px: '25px',
            py: '20px',
            [MOBILE_SX]: { px: '16px', py: '8px' }
          }}
        >
          {localPrograms.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <Typography sx={{ color: '#868686', fontFamily: 'Inter, sans-serif', fontSize: 14 }}>
                {memberName} is not enrolled in any programs
              </Typography>
            </Box>
          ) : isMobileApp ? (
            localPrograms.map((program, index) => {
              const flags = program.unresolvedFlags ?? 0;
              return (
                <Box
                  key={program.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    py: '14px',
                    borderTop: index > 0 ? '1px solid #2A2A2A' : 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                    <Typography sx={{ ...cellTextSx, color: '#B6B6B6', flexShrink: 0 }}>{index + 1}</Typography>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ ...cellTextSx, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                        {program.name}
                      </Typography>
                      <Typography
                        sx={{
                          ...cellTextSx,
                          fontWeight: 400,
                          fontSize: 12.5,
                          color: '#868686',
                          mt: '2px',
                          whiteSpace: 'normal'
                        }}
                      >
                        {formatProgramModel(program.programModel)}
                      </Typography>
                    </Box>
                    {removableFrom(program) && <Box sx={{ alignSelf: 'center' }}>{removeButton(program)}</Box>}
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '10px 24px', pl: '20px' }}>
                    <Box sx={{ minWidth: 140 }}>
                      <Typography sx={{ ...headerLabelSx, fontSize: 11, mb: '4px' }}>Role</Typography>
                      {roleCell(program)}
                    </Box>
                    <Box>
                      <Typography sx={{ ...headerLabelSx, fontSize: 11, mb: '4px' }}>Progress</Typography>
                      {program.adminOnly ? dash : <ProgressBar score={program.progress} />}
                    </Box>
                    <Box>
                      <Typography sx={{ ...headerLabelSx, fontSize: 11, mb: '4px' }}>Quiz score</Typography>
                      {program.adminOnly ? dash : <Typography sx={cellTextSx}>{quizScoreText(program)}</Typography>}
                    </Box>
                    {showFlags && (
                      <Box>
                        <Typography sx={{ ...headerLabelSx, fontSize: 11, mb: '4px' }}>Red flags</Typography>
                        {program.adminOnly ? (
                          dash
                        ) : flags > 0 && memberId ? (
                          <MetricButton tip="View red flags" color="#FF6A55" onClick={() => setRiskProgram(program)}>
                            <FlagIcon sx={{ fontSize: 14, color: '#FF6A55' }} />
                            {flags}
                          </MetricButton>
                        ) : (
                          <Typography sx={{ ...cellTextSx, color: flags > 0 ? '#FF6A55' : '#565656' }}>
                            {flags}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })
          ) : (
            <Box sx={{ overflow: 'hidden' }}>
              {/* Column headers */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '28px', pt: '7px', pb: '7px' }}>
                <Box sx={NAME_COL}>
                  <Typography
                    sx={{
                      ...headerLabelSx,
                      fontSize: 18,
                      fontWeight: 700,
                      lineHeight: '21.60px',
                      width: 24,
                      textAlign: 'center'
                    }}
                  >
                    #
                  </Typography>
                  <Typography sx={headerLabelSx}>Name</Typography>
                </Box>
                <Box sx={{ width: 140, flexShrink: 0 }}>
                  <Typography sx={headerLabelSx}>Program</Typography>
                </Box>
                <Box sx={{ width: 140, flexShrink: 0 }}>
                  <Typography sx={headerLabelSx}>Role</Typography>
                </Box>
                <Box sx={{ width: 150, flexShrink: 0 }}>
                  <Typography sx={headerLabelSx}>Progress</Typography>
                </Box>
                <Box
                  sx={{
                    width: 100,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px'
                  }}
                >
                  <Typography sx={headerLabelSx}>Quiz score</Typography>
                  <TapTooltip
                    title="Change in scores are recorded when a learner retakes their quiz assessments."
                    placement="bottom"
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 15, color: '#B6B6B6', cursor: 'default' }} />
                  </TapTooltip>
                </Box>
                {showFlags && (
                  <Box sx={{ width: 140, flexShrink: 0 }}>
                    <Typography sx={{ ...headerLabelSx, textAlign: 'center' }}>Unresolved red flags</Typography>
                  </Box>
                )}
                {manage && <Box sx={{ width: 28, flexShrink: 0 }} />}
              </Box>

              {/* Rows */}
              {localPrograms.map((program, index) => {
                const flags = program.unresolvedFlags ?? 0;
                return (
                  <Box
                    key={program.id}
                    sx={{ display: 'flex', alignItems: 'center', gap: '28px', pt: '12px', pb: '12px' }}
                  >
                    <Box sx={NAME_COL}>
                      <Typography
                        sx={{
                          fontSize: 18,
                          fontWeight: 700,
                          color: '#B6B6B6',
                          fontFamily: 'Inter, sans-serif',
                          lineHeight: '21.60px',
                          width: 24,
                          textAlign: 'center',
                          flexShrink: 0
                        }}
                      >
                        {index + 1}
                      </Typography>
                      <Typography sx={{ ...cellTextSx }}>{program.name}</Typography>
                    </Box>
                    <Box sx={{ width: 140, flexShrink: 0 }}>
                      <Typography sx={cellTextSx}>{formatProgramModel(program.programModel)}</Typography>
                    </Box>
                    <Box sx={{ width: 140, flexShrink: 0 }}>{roleCell(program)}</Box>
                    <Box sx={{ width: 150, flexShrink: 0 }}>
                      {program.adminOnly ? dash : <ProgressBar score={program.progress} />}
                    </Box>
                    <Box sx={{ width: 100, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                      {/* A-3: rationale-enabled programs show the first-pass →
                          current transition, but only once a retake has changed
                          the score. Unchanged (and legacy) programs show the
                          single original score. An admin only: nothing to score. */}
                      {program.adminOnly ? (
                        dash
                      ) : program.rationaleEnabled &&
                      typeof program.firstPassScore === 'number' &&
                      typeof program.currentScore === 'number' &&
                      program.firstPassScore !== program.currentScore ? (
                        <Typography sx={{ ...cellTextSx, textAlign: 'center' }}>
                          <Box component="span" sx={{ color: '#B6B6B6', fontWeight: 400 }}>
                            {program.firstPassScore}%
                          </Box>
                          {' → '}
                          {program.currentScore}%
                        </Typography>
                      ) : (
                        <Typography sx={{ ...cellTextSx, textAlign: 'center' }}>
                          {/* Null: assessed on nothing in this program (DA-5056 R5), a dash rather than 0%. */}
                          {scoreOrDash(
                            program.rationaleEnabled && typeof program.firstPassScore === 'number'
                              ? program.firstPassScore
                              : program.mcqScore
                          )}
                        </Typography>
                      )}
                    </Box>
                    {/* Unresolved red flags — click through to this program's red flags */}
                    {showFlags && (
                      <Box
                        sx={{
                          width: 140,
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {program.adminOnly ? (
                          dash
                        ) : flags > 0 && memberId ? (
                          <MetricButton tip="View red flags" color="#FF6A55" onClick={() => setRiskProgram(program)}>
                            <FlagIcon sx={{ fontSize: 14, color: '#FF6A55' }} />
                            {flags}
                          </MetricButton>
                        ) : (
                          <Typography sx={{ ...cellTextSx, color: flags > 0 ? '#FF6A55' : '#565656' }}>
                            {flags}
                          </Typography>
                        )}
                      </Box>
                    )}
                    {manage && (
                      <Box sx={{ width: 28, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
                        {removableFrom(program) && removeButton(program)}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Footer: allocate to a program (manage mode) — always visible */}
        {manage && (
          <Box
            sx={{
              flexShrink: 0,
              px: '25px',
              py: '16px',
              borderTop: '1px solid #2A2A2A',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              // A phone: the program on a row of its own, then the role and Add.
              [MOBILE_SX]: { flexWrap: 'wrap', px: '16px', pb: 'calc(16px + env(safe-area-inset-bottom))' }
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 500,
                fontFamily: 'Inter, sans-serif',
                color: '#FDFDFD',
                whiteSpace: 'nowrap'
              }}
            >
              Add to program
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0, [MOBILE_SX]: { flex: '1 1 100%' } }}>
              <ModalSelect
                options={addCandidates.map((p) => ({ value: p.id, label: p.name }))}
                value={addProgramId}
                onChange={setAddProgramId}
                placeholder="Select a program..."
                emptyText="Already in every program"
              />
            </Box>
            <Box sx={{ width: 160, flexShrink: 0, [MOBILE_SX]: { width: 'auto', flex: 1, minWidth: 0 } }}>
              <ModalSelect options={roleOptions} value={addRole} onChange={setAddRole} />
            </Box>
            <DosenButton
              onClick={handleAdd}
              disabled={busy || !addProgramId}
              loading={busy}
              loadingText="Adding..."
              tone="cta"
              styleVariant="filled"
              sizeVariant="lg"
              sx={{ minWidth: 100, height: 40, flexShrink: 0 }}
            >
              Add
            </DosenButton>
          </Box>
        )}
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        title={removing ? removalTitle(memberName, removing.name) : ''}
        // However they joined, a removal by hand keeps every automatic way from putting them back.
        message={removalMessage({ fromAudience: true, holdsProgramRole: false })}
        confirmLabel="Remove"
        cancelLabel="Cancel"
        busy={removeBusy}
        busyLabel="Removing..."
        onConfirm={handleRemove}
        onClose={() => setConfirmOpen(false)}
      />

      {riskProgram && memberId && (
        <MemberRiskAreasModal
          open={Boolean(riskProgram)}
          onClose={() => setRiskProgram(null)}
          memberName={memberName}
          memberId={memberId}
          programs={[riskProgram]}
        />
      )}
    </>
  );
};

export default MemberProgramsModal;
